<template>
  <div
    class="pagination flex items-center justify-center p-4 gap-1"
    v-if="pagination?.total > 0"
  >
    <span
      v-if="pagination?.showCount"
      class="text_color text-sm font-normal mr-2"
    >
      {{ rangeStart }}-{{ rangeEnd }} of {{ pagination.total }}
    </span>
    <button @click="prevPage" :disabled="pagination.current === 1">
      <ThemeImage
        class="size-4"
        :icon="imageNames.LEFT_ARROW"
        alt="left_arrow"
      />
    </button>
    <template v-if="!isDeepPagination">
      <template v-for="(page, index) in pageNumbers" :key="index">
        <span v-if="page === 'ellipsis'" class="pagination_ellipsis">•••</span>
        <button
          v-else
          @click="changePage(page)"
          :class="{ active: page === pagination.current }"
        >
          {{ page }}
        </button>
      </template>
    </template>
    <template v-else>
      <button
        v-if="pagination.current > 2"
        @click="changePage(1)"
      >
        1
      </button>
      <span v-if="pagination.current > 3" class="pagination_ellipsis">•••</span>
      <button
        v-if="pagination.current > 1"
        @click="changePage(pagination.current - 1)"
      >
        {{ pagination.current - 1 }}
      </button>
      <button class="active">
        {{ pagination.current }}
      </button>
      <button
        v-if="pagination.current < totalPages"
        @click="changePage(pagination.current + 1)"
      >
        {{ pagination.current + 1 }}
      </button>
    </template>
    <button @click="nextPage" :disabled="pagination.current === totalPages">
      <ThemeImage
        class="size-4"
        :icon="imageNames.RIGHT_ARROW"
        alt="right_arrow"
      />
    </button>

    <CustomDropdown
      v-if="allowChangePageSize"
      v-model="selectedPerPage"
      maxHeight="200px"
      gap="10px"
      position="top"
    >
      <template #trigger>
        <div
          class="flex flex-row gap-1 items-center text_color text-sm font-normal cursor-pointer ml-2"
        >
          {{ selectedPerPage }} per page

          <ThemeImage
            class="size-5"
            :icon="imageNames.ARROW_DOWN_TOOLTIP_GREY"
            alt="bottom_arrow"
          />
        </div>
      </template>

      <template #default>
        <div
          v-for="item in perPageOptions"
          :key="item"
          class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
          @click="updatePerPage(item)"
        >
          <span class="proxima_nova text-sm">{{ item }} per page</span>
        </div>
      </template>
    </CustomDropdown>

    <div v-if="showGoToPage && !isDeepPagination" class="go-to-page flex items-center gap-2 ml-4">
      <span class="text_color text-sm font-normal">Go to</span>
      <input
        type="number"
        v-model.number="goToPageInput"
        class="go-to-input"
        :min="1"
        :max="totalPages"
        @keyup.enter="goToPage"
        @blur="goToPage"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import { computed, ref, watch } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { message } from "ant-design-vue";

const DEFAULT_MAX_DATA_LIMIT = 10000;

const props = defineProps({
  pagination: Object,
  allowChangePageSize: { type: Boolean, required: false, default: true },
  perPageOptions: { type: Object, default: [20, 50, 100] },
  showGoToPage: { type: Boolean, required: false, default: false },
});

const emit = defineEmits(["update:pagination"]);

const selectedPerPage = ref(props.pagination?.pageSize || 20);
const perPageOptions = props.perPageOptions;
const goToPageInput = ref<number | null>(null);

watch(
  () => props.pagination?.current,
  (newVal) => {
    goToPageInput.value = null;
  },
);

const updatePerPage = (item) => {
  selectedPerPage.value = item;
  emit("update:pagination", {
    ...props.pagination,
    pageSize: item,
    current: 1,
    selection: true,
  });
};

const dataLimit = computed(() => props.pagination?.maxDataLimit ?? DEFAULT_MAX_DATA_LIMIT);

const maxPage = computed(() =>
  Math.ceil(dataLimit.value / props.pagination.pageSize),
);

const deepPaginationMaxPage = computed(() => {
  const threshold = props.pagination?.deepPaginationThreshold;
  if (!threshold) return null;
  return Math.floor(threshold / props.pagination.pageSize);
});

const totalPages = computed(() => {
  const calculatedPages = Math.ceil(
    props.pagination.total / props.pagination.pageSize,
  );
  if (props.pagination?.deepPaginationThreshold) {
    return calculatedPages;
  }
  return Math.min(calculatedPages, maxPage.value);
});

const isDeepPagination = computed(() => {

  if (!deepPaginationMaxPage.value) return false;
  return props.pagination.current >= deepPaginationMaxPage.value;
});

const nextPage = () => {
  if (props.pagination.current < totalPages.value) {
    emit("update:pagination", {
      ...props.pagination,
      current: props.pagination.current + 1,
      selection: false,
    });
  }
};

const prevPage = () => {
  if (props.pagination.current > 1) {
    emit("update:pagination", {
      ...props.pagination,
      current: props.pagination.current - 1,
      selection: false,
    });
  }
};

const changePage = (page) => {

  if (deepPaginationMaxPage.value) {
    const current = props.pagination.current;
    const isAdjacent = page === current + 1 || page === current - 1;
    if (page > deepPaginationMaxPage.value && !isAdjacent) {
      return;
    }
  }
  emit("update:pagination", {
    ...props.pagination,
    current: page,
    selection: false,
  });
};

const pageNumbers = computed(() => {
  const total = deepPaginationMaxPage.value
    ? Math.min(totalPages.value, deepPaginationMaxPage.value)
    : totalPages.value;
  const current = props.pagination.current;
  const range: (number | string)[] = [];
  const showFirstLast = props.pagination?.showFirstLastPage === true;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      range.push(i);
    }
  } else if (showFirstLast) {
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) {
        range.push(i);
      }
      range.push("ellipsis");
      range.push(total);
    } else if (current >= total - 3) {
      range.push(1);
      range.push("ellipsis");
      for (let i = total - 4; i <= total; i++) {
        range.push(i);
      }
    } else {
      range.push(1);
      range.push("ellipsis");
      for (let i = current - 1; i <= current + 1; i++) {
        range.push(i);
      }
      range.push("ellipsis");
      range.push(total);
    }
  } else {
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) {
        range.push(i);
      }
    } else {
      range.push(1);
      range.push("ellipsis");
      const start = current - 1;
      const end = Math.min(current + 3, total);
      for (let i = start; i <= end; i++) {
        range.push(i);
      }
    }
  }
  return range;
});

const rangeStart = computed(() => {
  return (props.pagination.current - 1) * props.pagination.pageSize + 1;
});

const rangeEnd = computed(() => {
  const end = props.pagination.current * props.pagination.pageSize;
  return Math.min(end, props.pagination.total);
});

const goToPage = () => {
  if (goToPageInput.value === null || goToPageInput.value === undefined) return;

  const page = Math.floor(goToPageInput.value);

  if (deepPaginationMaxPage.value && page > deepPaginationMaxPage.value) {
    message.warning(`Cannot jump beyond page ${deepPaginationMaxPage.value}. Use next/prev buttons.`);
    goToPageInput.value = null;
    return;
  }
  if (!deepPaginationMaxPage.value && page > maxPage.value) {
    message.warning(`Page number cannot exceed ${maxPage.value}`);
    goToPageInput.value = null;
    return;
  }

  if (
    page >= 1 &&
    page <= totalPages.value &&
    page !== props.pagination.current
  ) {
    emit("update:pagination", {
      ...props.pagination,
      current: page,
      selection: false,
    });
  }
  goToPageInput.value = null;
};
</script>
<style>
.pagination button {
  display: flex;
  height: 32px;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;

  color: var(--foreground-rgb);
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.pagination button:disabled {
  cursor: not-allowed;
}

.pagination button:hover:not(:disabled) {
  cursor: pointer;
}

.pagination button.active {
  border-radius: 8px;
  border: 1px solid var(--white-opacity-12);
  background: var(--white-opacity-8);
}

.pagination_ellipsis {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 4px;
  color: var(--secondary_color);
  font-size: 12px;
  letter-spacing: 1px;
  cursor: default;
  user-select: none;
}

.pagination span {
  margin: 0 8px;
}

.go-to-input {
  width: 48px;
  height: 32px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid var(--white-opacity-12);
  background: var(--white-opacity-8);
  color: var(--foreground-rgb);
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
  text-align: center;
  outline: none;
}

.go-to-input::-webkit-outer-spin-button,
.go-to-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.go-to-input[type="number"] {
  -moz-appearance: textfield;
}
</style>
